import { useMemo } from 'react';
import { cn } from '@rocco/utils/cn';
import { Avatar, AvatarFallback, AvatarImage } from '@rocco/components/avatar';
import type { Member, Professional } from 'generated/graphql';
import type { MeUser } from '~/server/types';
import { getUserInitials } from '~/modules/user';

type MeType = Pick<
    MeUser,
    '__typename' | 'ID' | 'Name' | 'Initials' | 'ProfileImage'
>;
type UserType = Pick<
    Member,
    '__typename' | 'ID' | 'Name' | 'Initials' | 'ProfileImageLink'
>;
type ProfessionalType = Pick<
    Professional,
    '__typename' | 'ID' | 'Title' | 'CompanyLogo' | 'LogoBackgroundColor'
>;
type Participant = MeType | UserType | ProfessionalType;

/**
 * The avatar component for a conversation participant. The participant can be
 * a user (member) or a professional.
 */
export function ParticipantAvatar({
    className,
    imageClassName,
    fallbackClassName,
    participant,
    singleLetterFallback = false,
    ...props
}: {
    className?: string;
    imageClassName?: string;
    fallbackClassName?: string;
    participant?: Participant;
    singleLetterFallback?: boolean;
} & React.ComponentPropsWithoutRef<typeof Avatar>) {
    const isProfessional = participant?.__typename === 'Professional';
    const imageSrc = isProfessional
        ? participant.CompanyLogo
        : participant?.__typename === 'Member'
          ? participant.ProfileImageLink
          : participant?.ProfileImage;
    const fallback = useMemo(() => {
        const initials = isProfessional
            ? participant.Title[0]?.toUpperCase()
            : participant
              ? getUserInitials(participant)
              : '';
        return singleLetterFallback ? initials?.[0] : initials;
    }, [isProfessional, participant, singleLetterFallback]);

    return (
        <Avatar
            className={cn(
                'transition-[width,height] duration-100 will-change-[width,height]',
                className
            )}
            {...props}
        >
            <AvatarImage
                // Add white background for images with transparent background.
                className={cn('bg-white', imageClassName)}
                src={imageSrc || undefined}
                alt={isProfessional ? participant.Title : participant?.Name}
                fit={isProfessional ? 'contain' : 'cover'}
                style={
                    isProfessional
                        ? {
                              // Professionals without a logo background color defaults to white.
                              backgroundColor:
                                  participant.LogoBackgroundColor ?? 'white',
                          }
                        : undefined
                }
            />
            <AvatarFallback
                className={cn(
                    'transition-[font-size] duration-100 will-change-[font-size]',
                    fallbackClassName
                )}
                delayMs={0}
            >
                {fallback}
            </AvatarFallback>
        </Avatar>
    );
}
